// @ts-strict-ignore

import { Mention, MentionLegacy, Message } from "rainbow-web-sdk/models/message.model";
import { AuthService } from "rainbow-web-sdk/services/core/auth.service";
import { ErrorHelperService } from "rainbow-web-sdk/services/utilities/errorHelper.service";
import { Conversation } from "rainbow-web-sdk/models/conversation.model";
import { ContactService } from "rainbow-web-sdk/services/core/contact.service";
import { LoggerService } from "rainbow-web-sdk/services/core/logger.service";
import { Service } from "rainbow-web-sdk/services/service";
import { XmppService } from "../core";
import { RBEvent } from "../../models/event.model";
import xmlParser from "../../utils/xmlParser";
import { Subject, Subscription } from "rxjs";
import { BubbleServiceEvents, BubbleServiceRB } from "../bubbles/bubble.service";
import type { StropheHandler } from 'rainbow-web-sdk';

export const NOTIFCENTER_SVC = 'NotificationCenterServiceRB';

export interface NotificationCenterService {
    /** Get the list of the current notifications inside the notification center */
    getNotifications(): Promise<NotificationCenterItem[]>;

    /** Mark a given notification as READ
     * If the notification is already marked as READ, it does nothing
     * On success, an event will be received NotificationCenterEvents.ON_NOTIFICATION_ACKREAD_UPDATE
     * @param notification - the NotificationCenterItem to READ
     */
    markNotificationAsRead(notification: NotificationCenterItem): Promise<void>;

    /** Permanently deletes a given notification
     * On success, an event will be received NotificationCenterEvents.ON_NOTIFICATIONS_LIST_UPDATE
     * @param notification - the NotificationCenterItem to DELETE
     */
    deleteNotification(notification: NotificationCenterItem): Promise<void>;

    /**
     * Subscribe to updates from the service (all events are of RBEvent);
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     */
    subscribe(handler: (event: RBEvent<NotificationCenterEvents>) => any): Subscription;
}

/**
 * @eventProperty
 */
export enum NotificationCenterEvents {

    /**
     * @eventProperty
     * This RBEvent is send when the list of the notifications (or the content) has been updated;
     * This event is also send on start / reconnection, once the list is updated from the server
     */
    ON_NOTIFICATIONS_LIST_UPDATE = 'ON_NOTIFICATIONS_LIST_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is send when a given notification is updated
     * **Event Data**
     * - **notification** : The NotificationCenterItem that is being updated;
     */
    ON_NOTIFICATION_UPDATE = 'ON_NOTIFICATION_UPDATE'
}

export interface NotificationCenterItem {
    /** Notification unique identifier */
    id?: string;

    /** Type of that notification (for the moment only 'mention') */
    type?: string;

    /** Notification read status */
    isRead?: boolean;

    /** Owner of this notification */
    owner?: string;

    /** Creator of this notification */
    sender?: string;

    /** Object representing notification content, Object's differ depending on notification type */
    content?: {
        /** XMPP message id where notification owner has been mentioned  */
        messageId?: string;

        /** XMPP message body where notification owner has been mentioned (mandatory when type is mention) */
        messageText?: string;

        /** Unique identifier of the peer of that conversation  */
        peer?: string;

        /** Peer Jid of the conversation where notification owner has been mentioned */
        peerJid?: string;
    };

    /** Creation date of the Notification (read only, set automatically on notification creation) in string format.
     *  Can be transformed to Date with new Date(creationDate);
     */
    creationDate?: string;
}

export class NotificationCenterServiceRB extends Service implements NotificationCenterService {

    private logger: LoggerService = LoggerService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private bubbleService: BubbleServiceRB = BubbleServiceRB.getInstance();

    private started: boolean = false;
    private startingPromise: Promise<NotificationCenterItem[]>;
    private xmppSubscriptionHandlers: StropheHandler[] = [];
    private subscription: Subscription;

    private rxSubject: Subject<RBEvent<NotificationCenterEvents>> = new Subject<RBEvent<NotificationCenterEvents>>();

    public static getInstance() {
        return Service.create(NOTIFCENTER_SVC, NotificationCenterServiceRB);
    }

    public static build() {
        return new NotificationCenterServiceRB();
    }

    private constructor() {
        super();
        this.xmppService.subscribeToConnectionEvents((event: RBEvent) => { this.onConnectionStateChangeEvent(event); });
    }

    private notifications: NotificationCenterItem[] = [];

    /********************************************************/
    /** LIFE CYCLE STUFF                                   */
    /********************************************************/

    private onConnectionStateChangeEvent(event) {
        if (event.name === 'ON_XMPP_DISCONNECTED') {
            if (event.data.reason === "LOGOUT") {
                this.stop();
            }
        }
        else if (event.name === 'ON_XMPP_CONNECTED') {
            if (event.data.reason === 'RECONNECTION' && this.started) {
                this.logger.log('[NotificationCenterServiceRB] onConnectionStateChangeEvent : connected -- service already started, refresh notifications from server');
                this.getAllNotificationsFromServer();
            }
            this.attachHandlers();
        }
    }

    public attachHandlers() {
        this.logger.info(`[NotificationCenterServiceRB] attachHandlers`);
        this.xmppSubscriptionHandlers.forEach((handler) => { this.xmppService.deleteHandler(handler); });
        this.xmppSubscriptionHandlers = [];
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.onManagementMessageReceived(stanza); return true; }, 'jabber:client', 'message', 'management'));

        if (!this.subscription) {
            this.subscription = this.bubbleService.subscribe((event: RBEvent<BubbleServiceEvents>) => {
                if (event?.name === BubbleServiceEvents.ON_USER_BUBBLE_REMOVED) {
                    let sendEvent;
                    //check the current list of notifications and remove in case there's bubble related to that
                    if (this.notifications?.length) {
                        this.notifications.forEach((notification) => {
                            if (notification?.content?.peerJid === event?.data?.bubble?.jid) {
                                sendEvent = true;
                            }
                        });
                    }

                    if (sendEvent) {
                        this.sendEvent(NotificationCenterEvents.ON_NOTIFICATIONS_LIST_UPDATE);
                    }
                }
            });
        }
    }

    public async stop(): Promise<void> {
        this.notifications = [];
        this.started = false;
        this.xmppSubscriptionHandlers.forEach((handler) => { this.xmppService.deleteHandler(handler); });
        this.xmppSubscriptionHandlers = [];
        if (this.subscription) { this.subscription.unsubscribe(); this.subscription = undefined; }
        this.logger.info(`[NotificationCenterServiceRB] === STOPPED ===`);
    }

    public markAllMentionsRead() {
        const mentionNotif = this.notifications.filter(notif => notif.type === "mention");
        mentionNotif.forEach((notif) => {
            this.markNotificationAsRead(notif);
        });
    }

    /********************************************************/
    /** PUBLIC & PRIVATE APIS                               */
    /********************************************************/

    /** Get the list of the current notifications inside the notification center */
    public async getNotifications(): Promise<NotificationCenterItem[]> {
        if (!this.started) {
            if (this.startingPromise) return this.startingPromise;

            this.startingPromise = new Promise((resolve, reject) => {
                this.getAllNotificationsFromServer()
                    .then(() => {
                        resolve(this.notifications);
                        this.startingPromise = undefined;
                    })
                    .catch((error) => {
                        reject(error);
                        this.startingPromise = undefined;
                    });
            });

            return this.startingPromise;
        }
        return this.notifications;
    }

    private async getAllNotificationsFromServer(): Promise<void> {
        try {
            this.logger.info(`[NotificationCenterServiceRB] getAllNotificationsFromServer`);
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact?.dbId}/notificationcenter`;

            const response = await fetch(url, { method: 'GET', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;

            this.notifications = (await response.json()).data as NotificationCenterItem[];

            this.started = true;

            this.logger.info(`[NotificationCenterServiceRB] getAllNotificationsFromServer done ${this.notifications.length}`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'mentionUsersInConversation');
            this.logger.error(`[NotificationCenterServiceRB] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    /**
     * API USED BY CONVERSATION SERVICE
     * When a new message contains a list of mentions, we need to create the mentions on the server via this API where we send the list of all mentionned
     * rainbow users, the conversation, and the message itself
     */
    public async mentionUsersInConversation(mentions: (Mention | MentionLegacy)[], conversation: Conversation, message: Message) {
        try {
            if (!conversation.bubble || conversation.bubble?.isWebinarBubble()) throw new Error("[NotificationCenterServiceRB] mentionUsersInConversation -- mentions are only allowed in bubble converstations");
            const contacts = mentions.map((mention) => { return this.contactService.getContactFromCacheById(mention.dbId); });

            if (!contacts.length) throw new Error("[NotificationCenterServiceRB] mentionUsersInConversation -- no contacts are found to be mentioned");

            let contactsDbId = contacts.map((contact) => { return contact.dbId; });

            //remove duplicates of DB IDs
            contactsDbId = [...new Set(contactsDbId)];

            this.logger.info(`[NotificationCenterServiceRB] mentionUsersInConversation -- bubble ${conversation.bubble.dbId} -- for total users number ${contactsDbId.length}`);

            await this.innerUpdateMentionUsersInConversation(contactsDbId, conversation, message, "create");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'mentionUsersInConversation');
            this.logger.error(`[NotificationCenterServiceRB] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    /**
     * API USED BY CONVERSATION SERVICE
     * When a message is updated / deleted, we need to also update the mentions; We can either add, remove or do both in one single update of message
     */
    public async updateMentionedUserInConversation(conversation: Conversation, message: Message, oldMentions: (Mention | MentionLegacy)[] = [], newMentions: (Mention | MentionLegacy)[] = []) {
        try {
            this.logger.info(`[NotificationCenterServiceRB] updateMentionedUserInConversation -- bubble ${conversation.bubble.dbId}`);

            let mentionsToRemove = [];
            let mentionsToAdd = [];
            const promises = [];

            //first, we need to compare the 2 arrays
            if (!oldMentions.length && newMentions.length) {
                //we've ONLY added mentions
                mentionsToAdd = newMentions;
            }
            else if (oldMentions.length && !newMentions.length) {
                //all mentions are deleted
                mentionsToRemove = oldMentions;
            }
            else if (oldMentions.length && newMentions.length) {

                //compare both arrays
                for (const mention of oldMentions) {
                    if (newMentions.findIndex((newMention) => { return mention.dbId === newMention.dbId; }) === -1) {
                        mentionsToRemove.push(mention);
                    }
                }

                mentionsToAdd = newMentions;
            }

            if (mentionsToAdd.length) {
                promises.push(this.mentionUsersInConversation(mentionsToAdd, conversation, message));
            }

            if (mentionsToRemove.length) {
                promises.push(this.deleteMentionedUsersInConversation(mentionsToRemove, conversation, message));
            }

            await Promise.allSettled(promises);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateMentionedUserInConversation');
            this.logger.error(`[NotificationCenterServiceRB] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    /**
     * DELETE LIST OF MENTIONED USERS
     */
    private async deleteMentionedUsersInConversation(mentions: MentionLegacy[], conversation: Conversation, message: Message) {
        try {
            if (!conversation.bubble) throw new Error("[NotificationCenterServiceRB] deleteMentionedUsersInConversation -- mentions are only allowed in bubble converstations");
            const contacts = mentions.map((mention) => {
                if (mention.jid) return this.contactService.getContactFromCache(mention.jid);
                return this.contactService.getContactFromCache(mention.dbId);
            });

            if (!contacts.length) throw new Error("[NotificationCenterServiceRB] deleteMentionedUsersInConversation -- no contacts are found to be deleted");

            let contactsDbId = contacts.map((contact) => { return contact.dbId; });

            //remove duplicates of DB IDs
            contactsDbId = [...new Set(contactsDbId)];

            this.logger.info(`[NotificationCenterServiceRB] deleteMentionedUsersInConversation -- bubble ${conversation.bubble.dbId} -- for total users number ${contactsDbId.length}`);

            await this.innerUpdateMentionUsersInConversation(contactsDbId, conversation, message, "delete");
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'mentionUsersInConversation');
            this.logger.error(`[NotificationCenterServiceRB] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    //THE API is limited by 10 mentions at the time, so we need to manage several API calls in case of lot of mentions
    //rejects if there's one fail to send mentions
    private async innerUpdateMentionUsersInConversation(contactsDbId: string[], conversation: Conversation, message: Message, action: string = "create") {
        try {
            const promises = [];
            let listOfDbId = [];

            if (contactsDbId.length > 10) {
                listOfDbId = [];
                for (let i = 0; i < contactsDbId.length; i += 10)
                    listOfDbId.push(contactsDbId.slice(i, i + 10));
            }
            else {
                listOfDbId.push(contactsDbId);
            }

            listOfDbId.forEach((list) => {
                this.logger.info(`[NotificationCenterServiceRB] innerUpdateMentionUsersInConversation with action ${action} for bubble ${conversation.bubble.dbId} -- ${list}`);
                const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact?.dbId}/notificationcenter/mention`;
                if (action === "create") {
                    const body = {
                        mentioned: list,
                        messageText: message.data,
                        messageId: message.id,
                        peer: conversation.bubble.dbId,
                        peerJid: conversation.bubble.jid
                    };
                    promises.push(fetch(url, { method: 'POST', headers: this.authService.getPostHeader(), body: JSON.stringify(body) }));
                }
                else if (action === "delete") {
                    const body = {
                        mentioned: list,
                        messageId: message.id,
                        peerJid: conversation.bubble.jid
                    };
                    promises.push(fetch(url, { method: 'DELETE', headers: this.authService.getPostHeader(), body: JSON.stringify(body) }));
                }
            });

            await Promise.all(promises);

            this.logger.info(`[NotificationCenterServiceRB] innerUpdateMentionUsersInConversation -- bubble ${conversation.bubble.dbId} -- success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'innerUpdateMentionUsersInConversation');
            this.logger.error(`[NotificationCenterServiceRB] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    /**
     * Send to other services via sendEvent()
     * Can listen to it via notificationCenterService.subscribe() API
     * @param name - the name of the event
     * @param data -the data to be send (an object)
     */
    public sendEvent(name: NotificationCenterEvents, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    /**
     * Subscribe to events send from the Notification Center Service (all events are of RBEvent type {name: string, data:any});
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @returns Returns RxJS Subscription
     */
    public subscribe(handler): Subscription {
        return this.rxSubject.subscribe((event: RBEvent) => { try { handler(event); } catch {} });
    }

    /** Mark a given notification as READ
     * If the notification is already marked as READ, it does nothing
     * On success, an event will be received NotificationCenterEvents.ON_NOTIFICATION_ACKREAD_UPDATE
     * @param notification - the NotificationCenterItem to READ
     */
    public async markNotificationAsRead(notification: NotificationCenterItem) {
        if (notification?.isRead) return;

        try {
            this.logger.info(`[NotificationCenterServiceRB] markNotificationAsRead -- ${notification.id}`);
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact?.dbId}/notificationcenter/${notification.id}/read`;
            const response = await fetch(url, { method: 'PUT', headers: this.authService.getPostHeader() });
            if (!response.ok) throw response;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'markNotificationAsRead');
            this.logger.error(`[NotificationCenterServiceRB] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    /** Permanently deletes a given notification
     * On success, an event will be received NotificationCenterEvents.ON_NOTIFICATIONS_LIST_UPDATE
     * @param notification - the NotificationCenterItem to DELETE
     */
    public async deleteNotification(notification: NotificationCenterItem) {
        try {
            if (!notification) throw new Error("[NotificationCenterServiceRB] deleteNotification -- missing notification");

            this.logger.info(`[NotificationCenterServiceRB] deleteNotification -- ${notification.id}`);
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact?.dbId}/notificationcenter/${notification.id}`;
            const response = await fetch(url, { method: 'DELETE', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteNotification');
            this.logger.error(`[NotificationCenterServiceRB] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    /********************************************************/
    /** EVENTS MANAGEMENT                                   */
    /********************************************************/

    private onManagementMessageReceived(stanza) {
        try {
            const stanzaElem = xmlParser(stanza);
            const notificationcenterElem = stanzaElem.find("notificationcenter");
            if (notificationcenterElem) {
                let action = notificationcenterElem.attr("action");

                this.logger.info(`[NotificationCenterServiceRB] onManagementMessageReceived with action ${action}`);

                //see if we already have a notification with same id
                const id = notificationcenterElem.attr("id");

                if (action === "delete") {

                    const index = this.notifications.findIndex(item => item.id === id);
                    if (index >= 0) {
                        this.notifications.splice(index, 1);
                    }

                    this.sendEvent(NotificationCenterEvents.ON_NOTIFICATIONS_LIST_UPDATE);
                    return;
                }

                let notificationCenterItem = this.notifications.find(item => item.id === id);

                if (!notificationCenterItem) {
                    notificationCenterItem = {};
                    //if not found, switch the action to create, it should not happen, but anyway just in case
                    action = "create";
                }

                //get all the data
                notificationCenterItem.id = notificationcenterElem.attr("id");
                notificationCenterItem.isRead = Boolean(notificationcenterElem.attr("read") === "true");
                notificationCenterItem.owner = notificationcenterElem.attr("owner");
                notificationCenterItem.sender = notificationcenterElem.attr("sender");
                notificationCenterItem.type = notificationcenterElem.attr("type");

                if (!notificationCenterItem.creationDate) { notificationCenterItem.creationDate = new Date().toString(); }

                const content = stanzaElem.find("content");
                if (content) {
                    notificationCenterItem.content = JSON.parse(content.text());
                }

                if (action === "create") {
                //add notification in the list of notifications
                    this.notifications.push(notificationCenterItem);

                    this.sendEvent(NotificationCenterEvents.ON_NOTIFICATIONS_LIST_UPDATE);
                }
                else if (action === "update") {
                    this.sendEvent(NotificationCenterEvents.ON_NOTIFICATION_UPDATE, { notification: notificationCenterItem });
                }
            }
        }
        catch (error) {
            //do nothing
            this.logger.error(`[NotificationCenterServiceRB] onManagementMessageReceived error -- ${error}`);
        }
    }
}
